import {
  DeleteOutlined,
  InsertRowBelowOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
} from "antd";
import { TextAreaRef } from "antd/es/input/TextArea";
import * as React from "react";
import { classNames } from "src/common/classNames";
import {
  groupChecked,
  isChecked,
} from "src/components/ReactiveTable/utils/groupChecked";

import { AttrEditorField } from "../AttrEditorField";
import { makeNewOptionId } from "./makeNewOptionId";
import { OptionRecord } from "./OptionRecord";
import * as styles from "./OptionsEditor.module.less";
import { splitMassText } from "./splitMassText";

const optionsName: AttrEditorField = "inlineOptions";
const ri = (id: keyof OptionRecord): string => id;
const riName = (rowIndex: number, rowField: keyof OptionRecord) => [
  optionsName,
  rowIndex,
  rowField,
];

export const OptionsEditor: React.FC = () => {
  const curValue: OptionRecord[] | undefined = Form.useWatch(optionsName);
  const form = Form.useFormInstance();
  const onRadio = (index: number) => {
    curValue?.forEach(({ isDefault }, i) => {
      form.setFieldValue(
        riName(i, "isDefault"),
        i === index ? !isDefault : false,
      );
    });
  };
  const onSelect = (index: number) => {
    form.setFieldValue(riName(index, "selected"), !curValue?.[index]?.selected);
  };
  const totalSelect = groupChecked(
    curValue ?? [],
    ({ selected }) => !!selected,
  );
  const onTotalSelect = () => {
    const newSelected = totalSelect !== "all";
    curValue?.forEach((_, i) => {
      form.setFieldValue(riName(i, "selected"), newSelected);
    });
  };
  const onDelete = (remove: (delList: number[]) => void) => {
    const delList: number[] = [];
    curValue?.forEach(({ selected }, i) => {
      if (selected) delList.push(i);
    });
    remove(delList);
  };
  const [massText, setMassText] = React.useState<string | undefined>(undefined);
  const refMassText = React.useRef<TextAreaRef>(null);
  const onMassInsert = () => {
    setMassText("");
    setTimeout(() => {
      refMassText.current?.focus?.();
    }, 200);
  };
  const closeMassInsert = () => setMassText(undefined);
  const onMassInsertApply = (add: (row: OptionRecord) => void) => {
    if (massText) {
      const baseId = +makeNewOptionId(curValue ?? []);
      splitMassText(massText, baseId).forEach((row) => add(row));
    }
    closeMassInsert();
  };
  const ruleNonEmpty = {
    validator: (_rule: unknown, value: unknown): Promise<void> => {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return Promise.reject(Error("Требуется добавить хотя бы одну опцию"));
      }
      return Promise.resolve();
    },
  };
  return (
    <Form.List name={optionsName} rules={[ruleNonEmpty]}>
      {(fields, { add, remove }, { errors }) => (
        <div className={styles.frame}>
          <div className={styles.toolbar}>
            <div className={styles.label}>
              <span className={styles.star}>*</span>
              <span>Значение</span>
            </div>
            <Button
              icon={<DeleteOutlined />}
              danger
              disabled={totalSelect === "none"}
              onClick={() => onDelete(remove)}
            >
              Удалить
            </Button>
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                add({
                  id: makeNewOptionId(curValue ?? []),
                  label: "",
                  order: undefined,
                  isDefault: false,
                } satisfies OptionRecord);
              }}
            >
              Добавить
            </Button>
            <Button
              icon={<InsertRowBelowOutlined />}
              title={msgMassAdd}
              onClick={onMassInsert}
            />
          </div>
          <div style={{ color: "#e9033a" }}>
            <Form.ErrorList errors={errors} />
          </div>

          <div className={styles.table}>
            <div className={styles.th}>
              <Checkbox
                checked={isChecked(totalSelect)}
                indeterminate={totalSelect === "partial"}
                onClick={onTotalSelect}
              />
            </div>
            <div className={styles.th}>
              <span>ID</span>
            </div>
            <div className={styles.th}>
              <span>Наименование</span>
            </div>
            <div className={styles.th}>
              <span>Ранг</span>
            </div>
            <div className={styles.th}>
              <span>По умолчанию</span>
            </div>
            {fields.map((field, index) => (
              <React.Fragment key={field.key}>
                <div className={classNames([styles.td, styles.checker])}>
                  <Checkbox
                    checked={!!curValue?.[index]?.selected}
                    onClick={() => onSelect(index)}
                  />
                </div>
                <div className={classNames([styles.td, styles.id])}>
                  <span className={styles.linear} title={curValue?.[index]?.id}>
                    {curValue?.[index]?.id}
                  </span>
                </div>

                <Form.Item
                  {...field}
                  name={[field.name, ri("label")]}
                  className={styles.td}
                  rules={[{ required: true, message: "Необходимо заполнить" }]}
                >
                  <Input allowClear style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item
                  {...field}
                  name={[field.name, ri("order")]}
                  className={styles.td}
                >
                  <InputNumber />
                </Form.Item>

                <div className={classNames([styles.td, styles.checker])}>
                  <Radio
                    checked={curValue?.[index]?.isDefault}
                    onClick={() => onRadio(index)}
                  />
                </div>
              </React.Fragment>
            ))}
          </div>
          {fields.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Нет опций"
            />
          )}
          <Modal
            title={msgMassAdd}
            open={massText !== undefined}
            onCancel={closeMassInsert}
            onOk={() => onMassInsertApply(add)}
            centered
          >
            <Input.TextArea
              value={massText}
              onChange={(e) => setMassText(e.currentTarget.value)}
              rows={10}
              placeholder="Вставьте текст, где каждая строка соответствует одной опции"
              ref={refMassText}
            />
          </Modal>
        </div>
      )}
    </Form.List>
  );
};

const msgMassAdd = "Массовое добавление";
